/**
 * Pricing Configuration
 *
 * Prices are fetched from backend API which queries Stripe.
 * UI components must NEVER hardcode dollar amounts.
 * Stripe Price IDs are used only for checkout session creation (backend).
 *
 * RULES:
 * - NO hardcoded prices in UI
 * - NO client-side Stripe API calls (requires secret key)
 * - All price fetching goes through /api/prices backend endpoint
 */

// ============================================
// Stripe Price IDs (for checkout creation only)
// ============================================

/**
 * Stripe Price IDs for checkout session creation.
 * These are passed to backend checkout endpoints, never used to fetch prices client-side.
 * Values come from VITE_ env vars (safe to expose in frontend).
 */
export const STRIPE_PRICE_IDS = {
  subscription: {
    monthly: import.meta.env.VITE_STRIPE_PRICE_ID_MONTHLY || null,
    yearly: import.meta.env.VITE_STRIPE_PRICE_ID_YEARLY || null,
  },
  project: {
    single: import.meta.env.VITE_STRIPE_PRICE_ID_PROJECT || null,
  },
  lifetime: {
    standard: import.meta.env.VITE_STRIPE_PRICE_ID_LIFETIME || null,
  },
  credits: {
    starter: import.meta.env.VITE_STRIPE_PRICE_ID_CREDITS_STARTER || null,
    pro: import.meta.env.VITE_STRIPE_PRICE_ID_CREDITS_PRO || null,
    studio: import.meta.env.VITE_STRIPE_PRICE_ID_CREDITS_STUDIO || null,
  },
}

// ============================================
// Price Fetching
// ============================================

/**
 * @typedef {Object} PriceData
 * @property {{ monthly: number|null, yearly: number|null }} subscription
 * @property {{ single: number|null }} project
 * @property {{ standard: number|null }} lifetime
 * @property {{ starter: number|null, pro: number|null, studio: number|null }} credits
 */

/**
 * Fetch current display prices from backend.
 * Backend calls Stripe API with secret key and returns amounts in cents.
 *
 * @returns {Promise<PriceData|null>} Price data or null on error
 */
export async function fetchPrices() {
  try {
    const response = await fetch('/api/prices')

    if (!response.ok) {
      // 503 means Stripe not configured - expected in development
      if (response.status === 503) {
        console.warn('[pricing] Stripe not configured, prices unavailable')
        return null
      }
      throw new Error(`Failed to fetch prices: ${response.status}`)
    }

    const data = await response.json()
    return data
  } catch (error) {
    console.error('[pricing] Failed to fetch prices:', error)
    return null
  }
}

// ============================================
// Price Formatting
// ============================================

/**
 * Format price for display using Intl.NumberFormat.
 *
 * @param {number|null|undefined} cents - Price in cents, or null if unavailable
 * @param {Object} options - Formatting options
 * @param {string} [options.period] - Optional period label ('month', 'year', 'one-time')
 * @param {boolean} [options.showCents=false] - Whether to show cents (default: hide for whole dollars)
 * @returns {string} Formatted price or empty string if unavailable
 *
 * @example
 * formatPrice(999)           // "$9.99"
 * formatPrice(900)           // "$9"
 * formatPrice(7900, { period: 'year' }) // "$79/year"
 * formatPrice(null)          // ""
 */
export function formatPrice(cents, options = {}) {
  const { period = null, showCents = false } = options

  if (cents === null || cents === undefined) return ''

  const dollars = cents / 100

  // Determine if we should show decimal places
  const hasDecimal = cents % 100 !== 0
  const useDecimals = showCents || hasDecimal

  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: useDecimals ? 2 : 0,
    maximumFractionDigits: useDecimals ? 2 : 0,
  })

  const formatted = formatter.format(dollars)
  const periodSuffix = period ? `/${period}` : ''

  return `${formatted}${periodSuffix}`
}

/**
 * Calculate savings between two prices.
 *
 * @param {number|null} regularCents - Regular price in cents
 * @param {number|null} saleCents - Sale price in cents
 * @returns {{ amount: number, percent: number } | null} Savings or null if can't calculate
 */
export function calculateSavings(regularCents, saleCents) {
  if (
    regularCents === null ||
    regularCents === undefined ||
    saleCents === null ||
    saleCents === undefined ||
    regularCents <= saleCents
  ) {
    return null
  }

  const amount = regularCents - saleCents
  const percent = Math.round((amount / regularCents) * 100)

  return { amount, percent }
}

// ============================================
// Price Availability Checks
// ============================================

/**
 * Check if subscription prices are available.
 *
 * @param {PriceData|null} prices - Price data from fetchPrices()
 * @returns {boolean} True if at least monthly price is available
 */
export function hasSubscriptionPrices(prices) {
  return prices?.subscription?.monthly !== null
}

/**
 * Check if project price is available.
 *
 * @param {PriceData|null} prices - Price data from fetchPrices()
 * @returns {boolean} True if project price is available
 */
export function hasProjectPrice(prices) {
  return prices?.project?.single !== null
}

/**
 * Check if lifetime price is available.
 *
 * @param {PriceData|null} prices - Price data from fetchPrices()
 * @returns {boolean} True if lifetime price is available
 */
export function hasLifetimePrice(prices) {
  return prices?.lifetime?.standard !== null
}

/**
 * Check if any prices are available.
 *
 * @param {PriceData|null} prices - Price data from fetchPrices()
 * @returns {boolean} True if at least one price is available
 */
export function hasAnyPrices(prices) {
  return (
    hasSubscriptionPrices(prices) ||
    hasProjectPrice(prices) ||
    hasLifetimePrice(prices)
  )
}
